// stt-service.js
//
// Cliente del servicio Speech-to-Text self-hosted (faster-whisper-server)
// expuesto via nginx en /api/stt/. API compatible con OpenAI Whisper.
//
// Uso:
//   import sttService from '@/util/stt-service'
//   const blob = float32ToWav(samples, 16000)
//   const { text } = await sttService.transcribeBlob(blob, { language: 'es' })
//
// Singleton — el endpoint base se resuelve una vez al cargar el módulo y
// se reusa entre llamadas. No mantiene state (cada transcribeBlob es
// independiente).
//
// El POST se hace con axios para reusar el interceptor de cookies de
// sesión existente, aunque el endpoint /api/stt/ no requiere auth en
// el setup actual (lo expone nginx sin gating). Si en el futuro se
// agrega auth, basta cambiar la URL — fetch con credentials:'include'
// también funcionaría.

const STT_BASE = '/api/stt'

class SttService {
  constructor() {
    if (typeof SttService.instance === 'object') return SttService.instance
    SttService.instance = this
    this._lastError = null
  }

  // Transcribe un Blob WAV. Retorna { text, language, duration }.
  // Errores: lanza Error con .message del backend (e.g. modelo no
  // disponible, formato no soportado, audio vacío).
  //
  // opts:
  //   language: 'es' por default (locale principal del proyecto)
  //   model: opcional — si el server soporta múltiples modelos
  //   vadFilter: true por default — filtra silencios dentro del archivo
  //              (segmenta el audio en speech-only chunks antes de
  //              transcribir, mejorando precisión y velocidad)
  async transcribeBlob(blob, opts) {
    if (!blob || blob.size === 0) {
      throw new Error('STT: blob vacío')
    }
    opts = opts || {}
    const form = new FormData()
    form.append('file', blob, 'rx.wav')
    form.append('language', opts.language || 'es')
    form.append('response_format', 'json')
    form.append('vad_filter', String(opts.vadFilter !== false))
    if (opts.model) form.append('model', opts.model)

    const url = STT_BASE + '/v1/audio/transcriptions'
    let resp
    try {
      resp = await fetch(url, {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
    } catch (err) {
      this._lastError = err
      throw new Error('STT: error de red — ' + (err && err.message || 'desconocido'))
    }
    if (!resp.ok) {
      let detail = resp.statusText
      try {
        const body = await resp.json()
        if (body && (body.error || body.detail || body.message)) {
          detail = body.error || body.detail || body.message
        }
      } catch (_) { /* response no era JSON */ }
      this._lastError = new Error(detail)
      throw new Error('STT ' + resp.status + ': ' + detail)
    }
    const data = await resp.json()
    return {
      text: (data && data.text) || '',
      language: (data && data.language) || opts.language || 'es',
      duration: (data && data.duration) || 0,
    }
  }

  // Helper para probar el endpoint sin enviar audio. Útil para verificar
  // que el container está corriendo + el modelo está descargado.
  async healthCheck() {
    try {
      const resp = await fetch(STT_BASE + '/health', { method: 'GET' })
      return resp.ok
    } catch (_) {
      return false
    }
  }

  getLastError() { return this._lastError }
}

const instance = new SttService()
export default instance
export { SttService }
